import { useModel as _useModel, mergeModels as _mergeModels, defineComponent as _defineComponent } from 'vue'
import { unref as _unref, vModelText as _vModelText, normalizeClass as _normalizeClass, normalizeStyle as _normalizeStyle, createElementVNode as _createElementVNode, withDirectives as _withDirectives, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, Fragment as _Fragment, renderList as _renderList, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, renderSlot as _renderSlot, vShow as _vShow, withModifiers as _withModifiers } from "vue"

const _hoisted_1 = ["placeholder", "readonly"]
const _hoisted_2 = { class: "datepicker-ctrl" }
const _hoisted_3 = { class: "clearfix" }
const _hoisted_4 = { class: "datepicker-body" }
const _hoisted_5 = { class: "datepicker-weekRange" }
const _hoisted_6 = { class: "datepicker-dateRange" }
const _hoisted_7 = ["data-date", "onClick"]
const _hoisted_8 = { key: 2 }
const _hoisted_9 = {
  key: 0,
  class: "datepicker-footer"
}
const _hoisted_10 = { class: "btn-bar" }
const _hoisted_11 = ["disabled"]
const _hoisted_12 = { class: "datepicker-ctrl" }
const _hoisted_13 = { class: "datepicker-body" }
const _hoisted_14 = { class: "datepicker-monthRange" }
const _hoisted_15 = ["onClick"]
const _hoisted_16 = { class: "datepicker-ctrl" }
const _hoisted_17 = { class: "datepicker-body" }
const _hoisted_18 = { class: "datepicker-monthRange decadeRange" }
const _hoisted_19 = ["onClick"]

import { computed, reactive, ref, watch } from 'vue';
import { calendarLangFunc, type CalendarLang } from 'src/consts/calendar';
import {
  parse,
  detectDateEmitFormat,
  stringifyDecadeHeader,
  getYearHeaderValue,
} from 'src/lib/calendarHelper';
import { dtFormat } from 'src/lib/dateHelper';
import type { CalendarProps, CalendarEmit, CalendarState } from 'src/models/calendar';
import { useCalendarPane } from 'src/components/lib/Calendar/useCalendarPane';


export default /*@__PURE__*/_defineComponent({
  __name: 'index',
  props: /*@__PURE__*/_mergeModels({
    format: { default: 'mm/dd/yyyy' },
    inputClassObj: { default: () => ({}) },
    disabledDaysOfWeek: { default: () => [] },
    inputClearButton: { type: Boolean, default: false },
    clearButton2Disabled: { type: Boolean, default: false },
    readonly: { type: Boolean, default: false },
    lang: { default: navigator.language },
    placeholder: {},
    hasInput: { type: Boolean, default: true },
    pane: { default: 1 },
    borderWidth: { default: 2 },
    closeCalendarOnEnter: { type: Boolean, default: true },
    onDayClick: { type: Function, default: () => {} },
    changePane: { type: Function, default: () => {} },
    specialDays: { default: () => ({}) },
    disableSwitchMonthView: { type: Boolean, default: false },
    dayHeaderFormat: { default: 'm Y' },
    displayDatePickerFooter: { type: Boolean, default: true },
    dateInputBgColor: { default: 'white' }
  }, {
    "modelValue": {},
    "modelModifiers": {},
  }),
  emits: /*@__PURE__*/_mergeModels(["keyup"], ["update:modelValue"]),
  setup(__props: any, { emit: __emit }) {

const props = __props;

const emit = __emit;

const modelValue = _useModel<string>(__props, "modelValue");

const stringify = (date: Date, format?: string): string =>
  dtFormat(date, format ?? state.dateFormat);

const rawValueToInputValue = (val: string, format: string): string => {
  const parsedDate = parse(val, null);
  if (!parsedDate) return val;

  return stringify(parsedDate, format);
};

const state = reactive<CalendarState>({
  inputValue: rawValueToInputValue(modelValue.value ?? '', props.format),
  dateFormat: props.format,
  dateEmitFormat: detectDateEmitFormat(props.format),
  currDate: new Date(),
  dateRange: [],
  decadeRange: [],
  paneStyle: {
    width: '',
    right: '',
    position: 'absolute',
  },
  view: 'none',
});

const inputClassObjComputed = computed<Record<string, string>>(() => ({
  ...props.inputClassObj,
  'with-reset-button': props.inputClearButton ? 'with-reset-button' : '',
}));

const emitValue = computed<string>(() => {
  const parsedDate = parse(state.inputValue, null);
  if (!parsedDate) return state.inputValue;

  return stringify(parsedDate, state.dateEmitFormat);
});

const onInput = (): void => {
  modelValue.value = emitValue.value;
};

const clear = (): void => {
  state.inputValue = '';
  onInput();
};

const refDatepickerInput = ref<HTMLInputElement>();

const focusInput = (): void => {
  if (props.hasInput) {
    setTimeout(() => refDatepickerInput.value?.focus(), 0);
  }
};

const {
  rootEl,
  close,
  inputClick,
  switchMonthView,
  switchDecadeView,
  yearSelect,
  monthSelect,
  daySelect,
  preNextDecadeClick,
  preNextMonthClick,
  preNextYearClick,
  getItemClasses,
  getSpecialDay,
  stringifyDayHeader,
  setToday,
  parseDateWithDefault,
} = useCalendarPane(props, state, { stringify, onInput });

watch(modelValue, (val) => {
  state.inputValue = rawValueToInputValue(val ?? '', state.dateFormat);
  state.dateEmitFormat = detectDateEmitFormat(val ?? '');
});

const onInputKeydown = ($event: KeyboardEvent): void => {
  if ($event.key === 'Tab') close();
  if ($event.code === 'Space') {
    inputClick();
    $event.stopImmediatePropagation();
    $event.preventDefault();
  }
};

const onInputKeyup = ($event: KeyboardEvent): void => {
  if (props.closeCalendarOnEnter && $event.key === 'Enter') close();
  emit('keyup', $event);
};

const text = computed<CalendarLang>(() => calendarLangFunc(props.lang));

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", {
    class: "datepicker",
    ref_key: "rootEl",
    ref: rootEl
  }, [
    (__props.hasInput)
      ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
          _withDirectives(_createElementVNode("input", {
            type: "text",
            class: _normalizeClass(["form-control datepicker-input", inputClassObjComputed.value]),
            ref_key: "refDatepickerInput",
            ref: refDatepickerInput,
            placeholder: __props.placeholder,
            style: _normalizeStyle({'background-color': __props.dateInputBgColor}),
            readonly: __props.readonly,
            onClick: _cache[0] || (_cache[0] = 
//@ts-ignore
(...args) => (_unref(inputClick) && _unref(inputClick)(...args))),
            onInput: onInput,
            onKeydown: onInputKeydown,
            onKeyup: onInputKeyup,
            "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((state.inputValue) = $event))
          }, null, 46, _hoisted_1), [
            [_vModelText, state.inputValue]
          ]),
          (__props.inputClearButton && modelValue.value)
            ? (_openBlock(), _createElementBlock("button", {
                key: 0,
                type: "button",
                class: "close",
                onClick: _cache[2] || (_cache[2] = ($event: any) => {clear(); _unref(close)()})
              }, [...(_cache[14] || (_cache[14] = [
                _createElementVNode("span", null, "×", -1)
              ]))]))
            : _createCommentVNode("", true)
        ], 64))
      : _createCommentVNode("", true),
    _withDirectives(_createElementVNode("div", {
      class: "datepicker-popup",
      style: _normalizeStyle(state.paneStyle)
    }, [
      _createElementVNode("div", _hoisted_2, [
        _createElementVNode("span", {
          class: "datepicker-preBtn fas fa-chevron-left",
          "aria-hidden": "true",
          onClick: _cache[3] || (_cache[3] = ($event: any) => (_unref(preNextMonthClick)(0)))
        }),
        _createElementVNode("span", {
          class: "datepicker-nextBtn fas fa-chevron-right",
          "aria-hidden": "true",
          onClick: _cache[4] || (_cache[4] = ($event: any) => (_unref(preNextMonthClick)(1)))
        })
      ]),
      _createElementVNode("div", _hoisted_3, [
        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(__props.pane, (p, pan) => {
          return (_openBlock(), _createElementBlock("div", {
            class: "datepicker-inner",
            key: pan
          }, [
            _createElementVNode("div", _hoisted_4, [
              _createElementVNode("p", {
                class: "ym-disp1",
                onClick: _cache[5] || (_cache[5] = 
//@ts-ignore
(...args) => (_unref(switchMonthView) && _unref(switchMonthView)(...args)))
              }, _toDisplayString(_unref(stringifyDayHeader)(state.currDate, pan)), 1),
              _createElementVNode("div", _hoisted_5, [
                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(text.value.daysOfWeek, (w, i) => {
                  return (_openBlock(), _createElementBlock("span", { key: i }, _toDisplayString(w), 1))
                }), 128))
              ]),
              _createElementVNode("div", _hoisted_6, [
                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(state.dateRange[pan], (d, di) => {
                  return (_openBlock(), _createElementBlock("span", {
                    key: di,
                    class: _normalizeClass(["day-cell", _unref(getItemClasses)(d)]),
                    "data-date": stringify(d.date),
                    onClick: ($event: any) => (_unref(daySelect)(d.date, $event))
                  }, [
                    _createElementVNode("div", null, [
                      (d.sclass !== 'datepicker-item-gray')
                        ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                            _createTextVNode(_toDisplayString(_unref(getSpecialDay)(d.date) || d.text), 1)
                          ], 64))
                        : (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                            _createTextVNode(_toDisplayString(d.text), 1)
                          ], 64)),
                      (d.sclass !== 'datepicker-item-gray')
                        ? (_openBlock(), _createElementBlock("div", _hoisted_8, [
                            _renderSlot(_ctx.$slots, stringify(d.date))
                          ]))
                        : _createCommentVNode("", true)
                    ])
                  ], 10, _hoisted_7))
                }), 128))
              ])
            ])
          ]))
        }), 128))
      ]),
      (__props.displayDatePickerFooter)
        ? (_openBlock(), _createElementBlock("div", _hoisted_9, [
            _createElementVNode("div", _hoisted_10, [
              _createElementVNode("button", {
                class: "btn btn-sm btn-primary today",
                type: "button",
                onClick: _cache[6] || (_cache[6] = ($event: any) => {_unref(setToday)(); _unref(close)(); focusInput()})
              }, _toDisplayString(text.value.today), 1),
              _createElementVNode("button", {
                type: "button",
                class: _normalizeClass(["btn btn-sm btn-primary clear", {
              'btn-primary': !__props.clearButton2Disabled,
              'btn-default': __props.clearButton2Disabled,
            }]),
                disabled: __props.clearButton2Disabled,
                onClick: _cache[7] || (_cache[7] = ($event: any) => {clear(); _unref(close)(); focusInput()})
              }, _toDisplayString(text.value.clear), 11, _hoisted_11),
              _createElementVNode("button", {
                type: "button",
                class: "btn btn-sm btn-secondary close2",
                onClick: _cache[8] || (_cache[8] = ($event: any) => {_unref(close)(); focusInput()})
              }, _toDisplayString(text.value.close), 1)
            ])
          ]))
        : _createCommentVNode("", true)
    ], 4), [
      [_vShow, state.view === 'day']
    ]),
    _withDirectives(_createElementVNode("div", {
      class: "datepicker-popup",
      style: _normalizeStyle(state.paneStyle)
    }, [
      _createElementVNode("div", _hoisted_12, [
        _createElementVNode("span", {
          class: "datepicker-preBtn fas fa-chevron-left",
          "aria-hidden": "true",
          onClick: _cache[9] || (_cache[9] = ($event: any) => (_unref(preNextYearClick)(0)))
        }),
        _createElementVNode("span", {
          class: "datepicker-nextBtn fas fa-chevron-right",
          "aria-hidden": "true",
          onClick: _cache[10] || (_cache[10] = ($event: any) => (_unref(preNextYearClick)(1)))
        })
      ]),
      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(__props.pane, (p, pan) => {
        return (_openBlock(), _createElementBlock("div", {
          key: pan,
          class: "datepicker-inner"
        }, [
          _createElementVNode("div", _hoisted_13, [
            _createElementVNode("p", {
              onClick: _cache[11] || (_cache[11] = 
//@ts-ignore
(...args) => (_unref(switchDecadeView) && _unref(switchDecadeView)(...args)))
            }, _toDisplayString(_unref(getYearHeaderValue)(state.currDate, pan)), 1),
            _createElementVNode("div", _hoisted_14, [
              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(text.value.months, (m, $index) => {
                return (_openBlock(), _createElementBlock("span", {
                  key: $index,
                  class: _normalizeClass({'datepicker-dateRange-item-active':
                    (text.value.months[_unref(parseDateWithDefault)(modelValue.value ?? '').getMonth()]  === m) &&
                    state.currDate.getFullYear() + pan === _unref(parseDateWithDefault)(modelValue.value ?? '').getFullYear()}),
                  onClick: ($event: any) => (_unref(monthSelect)(_unref(getYearHeaderValue)(state.currDate, pan), $index))
                }, _toDisplayString(m.substr(0,3)), 11, _hoisted_15))
              }), 128))
            ])
          ])
        ]))
      }), 128))
    ], 4), [
      [_vShow, state.view === 'month']
    ]),
    _withDirectives(_createElementVNode("div", {
      class: "datepicker-popup",
      style: _normalizeStyle(state.paneStyle)
    }, [
      _createElementVNode("div", _hoisted_16, [
        _createElementVNode("span", {
          class: "datepicker-preBtn fas fa-chevron-left",
          "aria-hidden": "true",
          onClick: _cache[12] || (_cache[12] = ($event: any) => (_unref(preNextDecadeClick)(0)))
        }),
        _createElementVNode("span", {
          class: "datepicker-nextBtn fas fa-chevron-right",
          "aria-hidden": "true",
          onClick: _cache[13] || (_cache[13] = ($event: any) => (_unref(preNextDecadeClick)(1)))
        })
      ]),
      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(__props.pane, (p, pan) => {
        return (_openBlock(), _createElementBlock("div", {
          key: pan,
          class: "datepicker-inner"
        }, [
          _createElementVNode("div", _hoisted_17, [
            _createElementVNode("p", null, _toDisplayString(_unref(stringifyDecadeHeader)(state.currDate, pan)), 1),
            _createElementVNode("div", _hoisted_18, [
              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(state.decadeRange[pan], (decade, decadeIdx) => {
                return (_openBlock(), _createElementBlock("span", {
                  key: decadeIdx,
                  class: _normalizeClass({'datepicker-dateRange-item-active':
                    _unref(parseDateWithDefault)(state.inputValue).getFullYear() === decade.text
                  }),
                  onClick: _withModifiers(($event: any) => (_unref(yearSelect)(decade.text)), ["stop"])
                }, _toDisplayString(decade.text), 11, _hoisted_19))
              }), 128))
            ])
          ])
        ]))
      }), 128))
    ], 4), [
      [_vShow, state.view === 'year']
    ])
  ], 512))
}
}

})